import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Download, Plus, ExternalLink, Database, RefreshCw, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function DataImport() {
  const { api, isAdmin, currentUser } = useApp();
  const [configs, setConfigs] = useState([]);
  const [imports, setImports] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailImport, setDetailImport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ api_url: '', api_key: '', data_type: 'products' });

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const role = currentUser?.role || '';
      const [cRes, iRes] = await Promise.all([api.getImportConfigs(role), api.getImports()]);
      setConfigs(cRes.data);
      setImports(iRes.data);
    } catch (e) { console.error(e); }
  }, [api, isAdmin, currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleSaveConfig = async () => {
    if (!form.api_url) { toast.error('API URL is required'); return; }
    try {
      await api.saveImportConfig(form, currentUser?.role || '');
      toast.success('API configuration saved');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Failed to save config'); }
  };

  const handleFetchData = async (config) => {
    setLoading(true);
    try {
      const res = await api.fetchImportData({ api_url: config.api_url, api_key: config.api_key, data_type: config.data_type }, currentUser?.role || '');
      toast.success(`Data imported: ${res.data?.import?.record_count || 0} records`);
      load();
    } catch (e) {
      toast.error(`Import failed: ${e.response?.data?.detail || e.message}`);
    }
    setLoading(false);
  };

  if (!isAdmin) {
    return (
      <div data-testid="data-import-page" className="space-y-6 fade-in">
        <Card className="border border-border/60 shadow-sm">
          <CardContent className="p-12 text-center">
            <Download className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-700">Admin Access Only</h2>
            <p className="text-sm text-slate-400 mt-1">Data import is restricted to Business Head role only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const viewImportDetail = async (importId) => {
    try {
      const res = await api.getImportDetail(importId);
      setDetailImport(res.data);
    } catch (e) { toast.error('Failed to load details'); }
  };

  return (
    <div data-testid="data-import-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Data Import</h1>
          <p className="text-sm text-slate-500 mt-1">Import products, pricing, and revenue data from external APIs</p>
        </div>
        <Button data-testid="add-api-config-btn" onClick={() => { setForm({ api_url: '', api_key: '', data_type: 'products' }); setDialogOpen(true); }}
          className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
          <Plus className="w-4 h-4" /> Add API Source
        </Button>
      </div>

      {/* API Configurations */}
      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-3">API Sources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {configs.map(c => (
            <Card key={c.id} data-testid={`config-card-${c.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Database className="w-5 h-5 text-amber-700" />
                  </div>
                  <Badge variant="secondary" className="text-xs capitalize">{c.data_type}</Badge>
                </div>
                <p className="text-sm font-semibold text-slate-800 truncate" title={c.api_url}>{c.api_url}</p>
                <p className="text-xs text-slate-400 mt-1">Added: {new Date(c.created_at).toLocaleDateString()}</p>
                <div className="flex gap-2 mt-3">
                  <Button data-testid={`fetch-data-${c.id}`} size="sm" onClick={() => handleFetchData(c)} disabled={loading}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-xs">
                    {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                    Fetch Data
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {configs.length === 0 && (
            <Card className="col-span-full border border-dashed border-border/60">
              <CardContent className="p-8 text-center text-slate-400">
                <Database className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No API sources configured. Add one to start importing data.
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Import History */}
      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-3">Import History</h2>
        <Card className="border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {imports.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No imports yet</div>
            ) : (
              <div className="divide-y divide-border/30">
                {imports.map(imp => (
                  <div key={imp.id} data-testid={`import-row-${imp.id}`} className="flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Download className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800 truncate max-w-md">{imp.api_url}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="secondary" className="text-[10px] capitalize">{imp.data_type}</Badge>
                          <span className="text-xs text-slate-400">{imp.record_count} records</span>
                          <span className="text-xs text-slate-400">{new Date(imp.imported_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => viewImportDetail(imp.id)} className="hover:bg-amber-50">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Config Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="import-config-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Add API Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>API URL</Label>
              <Input data-testid="import-url-input" value={form.api_url} onChange={e => setForm({ ...form, api_url: e.target.value })}
                placeholder="https://api.example.com/products" />
            </div>
            <div>
              <Label>API Key (optional)</Label>
              <Input data-testid="import-key-input" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })}
                placeholder="Bearer token or API key" type="password" />
            </div>
            <div>
              <Label>Data Type</Label>
              <Select value={form.data_type} onValueChange={v => setForm({ ...form, data_type: v })}>
                <SelectTrigger data-testid="import-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="products">Products & Pricing</SelectItem>
                  <SelectItem value="revenue">Revenue Data</SelectItem>
                  <SelectItem value="users">User Details</SelectItem>
                  <SelectItem value="custom">Custom Data</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-config-btn" onClick={handleSaveConfig} className="bg-amber-600 hover:bg-amber-700 text-white">Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Detail Dialog */}
      <Dialog open={!!detailImport} onOpenChange={() => setDetailImport(null)}>
        <DialogContent data-testid="import-detail-dialog" className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Import Details</DialogTitle>
          </DialogHeader>
          {detailImport && (
            <div className="space-y-3 py-4">
              <div className="grid grid-cols-3 gap-3">
                <div><p className="text-xs text-slate-400">Type</p><Badge variant="secondary" className="text-xs capitalize">{detailImport.data_type}</Badge></div>
                <div><p className="text-xs text-slate-400">Records</p><p className="text-sm font-bold">{detailImport.record_count}</p></div>
                <div><p className="text-xs text-slate-400">Imported</p><p className="text-sm">{new Date(detailImport.imported_at).toLocaleString()}</p></div>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Data Preview</p>
                <div className="bg-slate-50 rounded-lg p-3 max-h-96 overflow-auto">
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap">{JSON.stringify(detailImport.data, null, 2)}</pre>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
